import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import FullscreenError from '@/components/utils/FullscreenError';
import CreateSymbolModal from '@/components/admin/recommendations/CreateSymbolModal.tsx';
import EditSymbolModal from '@/components/admin/recommendations/EditSymbolModal.tsx';
import OptionRefreshBadge from '@/components/admin/common/OptionRefreshBadge';
import JournalUnknownSymbolsPanel from '@/components/admin/symbols/JournalUnknownSymbolsPanel';
import { faChartLine, faSpinner, faArrowLeft, faPlus, faEye, faEdit, faTrash, faDisplayChartUpCircleDollar, faSearch, faXmark } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';
import { formatDate, formatCurrency } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { PAGINATION } from '@/config';
import type { Symbol, SymbolID, StockExchange, StockExchangeID } from '@/models';

const pageSize = 50;

/** Everything the option badge cannot show inline: when it last ran, how many contracts it
 *  produced and why it failed, if it did. */
function optionRefreshTitle(symbol: Symbol): string {
  const parts = ['Optionskontrakte'];

  if (symbol.lastOptionRefresh !== null) {
    parts.push(`zuletzt ${formatDate(symbol.lastOptionRefresh)}`);
  }
  parts.push(`${symbol.lastOptionRefreshCount} Kontrakte`);
  if (symbol.optionRefreshErrorCount > 0) {
    parts.push(`Fehler: ${symbol.optionRefreshErrorCount}`);
  }
  if (symbol.lastOptionRefreshError != null && symbol.lastOptionRefreshError !== '') {
    parts.push(symbol.lastOptionRefreshError);
  }

  return parts.join(' — ');
}

export default function AdminSymbolsList() {
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const [symbols, setSymbols] = useState<Symbol[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingSymbol, setEditingSymbol] = useState<Symbol | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [deletingSymbol, setDeletingSymbol] = useState<SymbolID | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [stockExchanges, setStockExchanges] = useState<StockExchange[]>([]);
  const [selectedStockExchangeId, setSelectedStockExchangeId] = useState<StockExchangeID | null>(null);
  const [showInTickerFilter, setShowInTickerFilter] = useState<boolean | null>(null);
  const [statusFilter, setStatusFilter] = useState<'okay' | 'warning' | 'error' | null>(null);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedStockExchangeId, showInTickerFilter, statusFilter]);

  // Fetch stock exchanges on mount
  useEffect(() => {
    const fetchStockExchanges = async () => {
      try {
        const response = await APIRoutes.listStockExchanges({ limit: PAGINATION.FETCH_ALL_LIMIT });
        setStockExchanges(response.stockExchanges);
      } catch (err) {
        console.error('Error loading stock exchanges:', err);
      }
    };
    fetchStockExchanges();
  }, []);

  const fetchSymbols = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      // Build status filter parameters
      let currentUpdateErrorCount: number | undefined = undefined;
      let currentUpdateErrorCountGreater: number | undefined = undefined;
      if (statusFilter === 'okay') {
        currentUpdateErrorCount = 0;
      } else if (statusFilter === 'warning') {
        currentUpdateErrorCountGreater = 0;
      } else if (statusFilter === 'error') {
        currentUpdateErrorCountGreater = 2;
      }

      const response = await APIRoutes.listSymbols({
        limit: pageSize,
        next: cursorToken,
        search: debouncedSearchTerm || undefined,
        stockExchangeId: selectedStockExchangeId ?? undefined,
        showInTicker: showInTickerFilter ?? undefined,
        currentUpdateErrorCount,
        currentUpdateErrorCountGreater,
      });
      setSymbols(response.symbols);
      setTotal(response.total);
    } catch (err) {
      console.error('Critical error loading symbols list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Symbolliste');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, selectedStockExchangeId, showInTickerFilter, statusFilter]);

  useEffect(() => {
    fetchSymbols();
  }, [fetchSymbols]);

  const handleSymbolCreated = (newSymbol: Symbol) => {
    setSymbols(prev => [newSymbol, ...prev]);
    setTotal(prev => prev + 1);
    showSuccessToast('Symbol erfolgreich erstellt');
  };

  const handleEditSymbol = (symbol: Symbol) => {
    setEditingSymbol(symbol);
    setIsEditModalOpen(true);
  };

  const handleSymbolUpdated = (updatedSymbol: Symbol) => {
    setSymbols(prevSymbols =>
      prevSymbols.map(symbol =>
        symbol.id === updatedSymbol.id ? updatedSymbol : symbol
      )
    );
    showSuccessToast('Symbol erfolgreich aktualisiert');
  };

  const handleDeleteSymbol = async (symbolId: SymbolID) => {
    if (!confirm('Sind Sie sicher, dass Sie dieses Symbol löschen möchten?')) {
      return;
    }

    try {
      setDeletingSymbol(symbolId);
      await APIRoutes.deleteSymbol(symbolId);
      setSymbols(prev => prev.filter(symbol => symbol.id !== symbolId));
      setTotal(prev => prev - 1);
      showSuccessToast('Symbol erfolgreich gelöscht');
    } catch (err) {
      console.error('Error deleting symbol:', err);
      showErrorToast('Fehler beim Löschen des Symbols');
      handleAPIError(err);
    } finally {
      setDeletingSymbol(null);
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Symbolliste nicht verfügbar"
        message={error || 'Die Symbolliste konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchSymbols}
        onBack={() => navigate('/admin')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to="/admin"
              className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <div className="flex items-center">
              <FontAwesomeIcon icon={faChartLine} className="w-8 h-8 text-cyan-600 dark:text-cyan-400 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Trading Symbole ({total})
              </h1>
            </div>
          </div>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!isAdmin}
            title={isAdmin ? 'Neues Symbol erstellen' : 'Nur für Administratoren'}
            className={`px-4 py-2 bg-cyan-600 text-white rounded-lg transition-colors flex items-center ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-cyan-700 cursor-pointer'}`}
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
            Neues Symbol
          </button>
        </div>

      <JournalUnknownSymbolsPanel onSymbolsCreated={() => { void fetchSymbols(); }} />

      {/* Filter Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col gap-4">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              />
              <input
                type="text"
                placeholder="Suche nach Ticker oder Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
              {searchTerm !== '' && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                >
                  <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Stock Exchange Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Börse
            </label>
            <div className="flex flex-wrap gap-2">
              {stockExchanges.map((exchange) => (
                <button
                  key={exchange.id}
                  onClick={() => setSelectedStockExchangeId(
                    selectedStockExchangeId === exchange.id ? null : exchange.id
                  )}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                    selectedStockExchangeId === exchange.id
                      ? 'bg-cyan-100 text-cyan-800 border-cyan-300 dark:bg-cyan-900/50 dark:text-cyan-400 dark:border-cyan-700'
                      : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                  }`}
                >
                  {exchange.identifier}
                </button>
              ))}
            </div>
          </div>

          {/* Properties Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Eigenschaften
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowInTickerFilter(showInTickerFilter === true ? null : true)}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                  showInTickerFilter === true
                    ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/50 dark:text-green-400 dark:border-green-700'
                    : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                }`}
              >
                Im Ticker enthalten
              </button>
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter(statusFilter === 'okay' ? null : 'okay')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                  statusFilter === 'okay'
                    ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/50 dark:text-green-400 dark:border-green-700'
                    : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                }`}
              >
                Okay
              </button>
              <button
                onClick={() => setStatusFilter(statusFilter === 'warning' ? null : 'warning')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                  statusFilter === 'warning'
                    ? 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/50 dark:text-yellow-400 dark:border-yellow-700'
                    : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                }`}
              >
                Warning
              </button>
              <button
                onClick={() => setStatusFilter(statusFilter === 'error' ? null : 'error')}
                className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                  statusFilter === 'error'
                    ? 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/50 dark:text-red-400 dark:border-red-700'
                    : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                }`}
              >
                Error
              </button>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(searchTerm !== '' || selectedStockExchangeId !== null || showInTickerFilter !== null || statusFilter !== null) && (
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedStockExchangeId(null);
                  setShowInTickerFilter(null);
                  setStatusFilter(null);
                }}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline cursor-pointer"
              >
                Alle Filter zurücksetzen
              </button>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-cyan-600 dark:text-cyan-400 animate-spin" />
        </div>
      )}

      {error && !criticalError && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={fetchSymbols}
              className="mt-2 text-red-600 dark:text-red-400 underline hover:text-red-800 dark:hover:text-red-300"
            >
              Erneut versuchen
            </button>
        </div>
      )}

      {!loading && !error && symbols.length === 0 && (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faChartLine} className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Keine Symbole gefunden
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {(searchTerm !== '' || selectedStockExchangeId !== null || showInTickerFilter !== null || statusFilter !== null)
                ? 'Keine Symbole gefunden, die den Filterkriterien entsprechen'
                : 'Erstellen Sie Ihr erstes Trading-Symbol.'}
            </p>
            {!(searchTerm !== '' || selectedStockExchangeId !== null || showInTickerFilter !== null || statusFilter !== null) && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                disabled={!isAdmin}
                title={isAdmin ? 'Erstes Symbol erstellen' : 'Nur für Administratoren'}
                className={`px-6 py-3 bg-cyan-600 text-white rounded-lg transition-colors ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-cyan-700 cursor-pointer'}`}
              >
                <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
                Erstes Symbol erstellen
              </button>
            )}
        </div>
      )}

      {!loading && symbols.length > 0 && (
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ticker
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Name + Branche
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Im Ticker
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aktueller Kurs
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Letzte Aktualisierung
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Erstellt
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {symbols.map((symbol) => (
                    <tr
                      key={symbol.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {symbol.exchange !== null ? (
                            <span title={symbol.exchange.name}>
                              {symbol.exchange.identifier}:{symbol.ticker}
                            </span>
                          ) : (
                            <span>{symbol.ticker}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          <span
                            className="block max-w-[200px] truncate font-medium"
                            title={symbol.name ?? ''}
                          >
                            {symbol.name ?? '-'}
                          </span>
                          <span
                            className="block max-w-[200px] truncate text-gray-500 dark:text-gray-400"
                            title={symbol.sector ?? ''}
                          >
                            {symbol.sector ?? '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {symbol.showInTicker ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Ja
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            Nein
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {symbol.currentData !== null ? (
                          <div className="text-sm">
                            <div className="text-gray-900 dark:text-white text-right tabular-nums font-medium">
                              {formatCurrency(symbol.currentData.price, symbol.currentData.currency)}
                            </div>
                            <div className={`text-right tabular-nums text-xs ${
                              (symbol.currentData.change ?? 0) >= 0
                                ? 'text-green-600 dark:text-green-400'
                                : 'text-red-600 dark:text-red-400'
                            }`}>
                              {symbol.currentData.change !== null && symbol.currentData.changePct !== null
                                ? `${symbol.currentData.change >= 0 ? '+' : ''}${symbol.currentData.change.toFixed(2)} (${symbol.currentData.changePct.toFixed(2)}%)`
                                : '-'}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {symbol.lastUpdated !== null ? formatDate(symbol.lastUpdated) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(symbol.created)}
                      </td>
                      {/* Kursdaten und Optionskontrakte werden von zwei unabhängigen Jobs
                          aktualisiert — ein Symbol kann bei den Kursen längst wieder gesund sein
                          und trotzdem seit Stunden keine Optionen mehr bekommen haben. */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col items-start gap-1">
                          {symbol.currentUpdateErrorCount === 0 ? (
                            <span
                              title={`Kursdaten — Fehler: ${symbol.currentUpdateErrorCount}`}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            >
                              Up-to-date
                            </span>
                          ) : symbol.currentUpdateErrorCount <= 2 ? (
                            <span
                              title={`Kursdaten — Fehler: ${symbol.currentUpdateErrorCount}`}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                            >
                              Warning
                            </span>
                          ) : (
                            <span
                              title={`Kursdaten — Fehler: ${symbol.currentUpdateErrorCount}`}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            >
                              Error
                            </span>
                          )}
                          <OptionRefreshBadge
                            status={symbol.lastOptionRefreshStatus}
                            title={optionRefreshTitle(symbol)}
                          />
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <Link
                            to={`/admin/symbols/${symbol.id}`}
                            className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-900 dark:hover:text-cyan-300 cursor-pointer"
                            title="Details anzeigen"
                          >
                            <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                          </Link>
                          {symbol.exchange !== null && (
                            <a
                              href={`https://www.tradingview.com/symbols/${symbol.exchange.identifier}-${symbol.ticker}/`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-600 dark:text-purple-400 hover:text-purple-900 dark:hover:text-purple-300"
                              title="Auf TradingView anzeigen"
                            >
                              <FontAwesomeIcon icon={faDisplayChartUpCircleDollar} className="w-4 h-4" />
                            </a>
                          )}
                          <button
                            onClick={() => handleEditSymbol(symbol)}
                            disabled={!isAdmin}
                            className={`text-blue-600 dark:text-blue-400 ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:text-blue-900 dark:hover:text-blue-300 cursor-pointer'}`}
                            title={isAdmin ? 'Bearbeiten' : 'Nur für Administratoren'}
                          >
                            <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteSymbol(symbol.id)}
                            disabled={!isAdmin || deletingSymbol === symbol.id}
                            className={`text-red-600 dark:text-red-400 disabled:opacity-50 ${!isAdmin ? 'cursor-not-allowed' : 'hover:text-red-900 dark:hover:text-red-300 cursor-pointer'}`}
                            title={isAdmin ? 'Löschen' : 'Nur für Administratoren'}
                          >
                            {deletingSymbol === symbol.id ? (
                              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                            ) : (
                              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={total}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              loading={loading}
            />
        </div>
      )}

      {/* Create Symbol Modal */}
      <CreateSymbolModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSymbolCreated={handleSymbolCreated}
        />

        {/* Edit Symbol Modal */}
        {editingSymbol && (
          <EditSymbolModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setEditingSymbol(null);
            }}
            symbol={editingSymbol}
            onSymbolUpdated={handleSymbolUpdated}
          />
        )}
    </>
  );
}
